'use client';

import { Button, Radio, StarRating } from '@petsmart-ui/sparky';
import debounce from 'lodash.debounce';
import {
    useClearRefinements,
    useNumericMenu,
    useStats,
} from 'react-instantsearch';

import type { FilterByRatingProps } from '@app/plp/components/FilterByRating';
import '@app/plp/components/FilterByRating/FilterByRating.scss';
import { INT_RADIX } from '@app/plp/constants/numeric';
import { closeButtonDropdown } from '@app/plp/utils/closeButtonDropdown';
import { FILTERS, getRatingValues } from '@libs/algolia';
import {
    dataLayerPush,
    trackPLPFilters,
    trackApplyFacetedFilters,
} from '@libs/analytics';
import { Divider } from '@libs/common-next';
import { useEffect, useMemo, useRef, useState } from 'react';

export default function FilterByRating({
    attribute,
    showActions,
    items,
    pageId,
}: FilterByRatingProps) {
    const { nbHits } = useStats();

    const { refine: clearRefine } = useClearRefinements({
        includedAttributes: [attribute],
    });

    const { items: filters, refine } = useNumericMenu({
        attribute,
        items,
    });

    const currentOption = useRef<number>(-1);
    const [hasLog, setHasLog] = useState<boolean>(false);

    const hasSelectedItem = useMemo(
        () => filters.some(filter => filter.isRefined),
        [filters]
    );

    const debounceLog = debounce(() => {
        setHasLog(true);
    }, 1200);

    const filterAnalytics = (value: string, label: string) => {
        const currentSelected = parseInt(label, INT_RADIX);
        if (currentOption.current !== currentSelected) {
            refine(value);
            currentOption.current = currentSelected;
            debounceLog();
        }
    };

    const clearRatingFilter = () => {
        currentOption.current = -1;
        clearRefine();
    };

    useEffect(() => {
        if (currentOption.current > 0) {
            const title = FILTERS.RATING.TITLE;
            const value = getRatingValues([
                {
                    label: '',
                    value: currentOption.current,
                },
            ]);
            dataLayerPush({
                event: FILTERS.EVENT.VALUE,
                filter_type: title,
                filter_selection: value,
                results: nbHits,
            });
            trackPLPFilters(title, value);
            trackApplyFacetedFilters(title, value, pageId);
            currentOption.current = -1;
            setHasLog(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only range deps
    }, [hasLog]);

    return (
        <div className="filter-by-rating" data-testid="filter-by-rating">
            <div className="filter-by-rating__item-container">
                {filters.map(({ value, label, isRefined }) => (
                    <button
                        data-testid={`filter-by-rating-value-${value}`}
                        type="button"
                        className={`filter-by-rating__item-container--item ${
                            isRefined
                                ? 'filter-by-rating__item-container--item__checked'
                                : ''
                        }`}
                        key={value}
                        onClick={() => filterAnalytics(value, label)}
                        tabIndex={parseInt(label, INT_RADIX)}
                    >
                        <Radio
                            onChange={() => null}
                            checked={isRefined}
                            data-testid={`filter-choice-type-radio-${
                                isRefined ? 'checked' : 'unchecked'
                            }`}
                        />
                        <StarRating
                            value={parseInt(label, INT_RADIX)}
                            className="sparky-u-margin-left-sm sparky-u-margin-right-sm"
                        />
                        <span>
                            {`${
                                parseInt(label, INT_RADIX) <
                                FILTERS.RATING.MAX_STARS
                                    ? FILTERS.RATING.UP
                                    : ''
                            }`}
                        </span>
                    </button>
                ))}
            </div>
            {showActions && (
                <div className="filter-by-rating__actions">
                    <Divider />
                    <Button
                        data-testid="filter-by-rating-button-reset-values"
                        fullWidth
                        onClick={clearRatingFilter}
                        text={FILTERS.CLEAR.TITLE}
                        type="button"
                        variant="link"
                        className="filter-by-rating__actions--reset sparky-u-padding-sm sparky-u-margin-bottom-sm"
                        disabled={!hasSelectedItem}
                    />
                    <Button
                        data-testid="filter-by-rating-button-close"
                        fullWidth
                        onClick={closeButtonDropdown}
                        text={FILTERS.VIEW_RESULTS.TITLE}
                        type="button"
                        variant="primary"
                        className="filter-by-rating__actions--view"
                    />
                </div>
            )}
        </div>
    );
}
